import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[15]

export const KWP_15_INSTALACION_FOTOVOLTAICA: ProgrammaticLandingPageConfig = {
  slug: 'instalacion-fotovoltaica-15-kwp-precio',
  lang: 'es',
  topicId: 'kwp-kosten-15',
  title: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos',
  metaDescription:
    '¿Cuánto cuesta una instalación fotovoltaica de 15 kWp? Superficie de tejado, número de ' +
    'paneles, desglose de costes y comparación gratuita para viviendas grandes con bomba de calor ' +
    'y coche eléctrico.',
  keywords: [
    'instalación fotovoltaica 15 kWp precio',
    'placas solares 15 kWp coste',
    'cuánto cuesta una instalación de 15 kWp',
    '15 kWp instalación solar vivienda grande',
  ],
  eyebrow: 'Tamaño de instalación: 15 kWp',
  h1: 'Instalación fotovoltaica de 15 kWp: precio y valores orientativos',
  intro:
    'Una instalación de 15 kWp es adecuada para viviendas unifamiliares grandes o viviendas ' +
    'bifamiliares con un consumo total elevado, por ejemplo por bomba de calor y coche eléctrico ' +
    'juntos. Aquí encontrará valores orientativos sobre superficie de tejado y número de paneles, ' +
    'un desglose de costes y justo debajo una calculadora con el precio medio para 15 kWp ya ' +
    'calculado a partir de presupuestos reales de empresas instaladoras.',
  calculatorPrefill: { kind: 'kwp', kwp: 15 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una instalación de 15 kWp?' },
    {
      type: 'paragraph',
      text:
        'Valor orientativo: 15 kWp suele encajar con una vivienda unifamiliar grande o bifamiliar ' +
        'con bomba de calor y coche eléctrico, consumo anual total a menudo entre 9.000 y ' +
        '12.000 kWh. Con dos viviendas independientes en el mismo edificio, conviene además ' +
        'asesorarse sobre cómo repartir la generación y el consumo entre ambas.',
    },
    { type: 'heading', text: 'Tejado y paneles: lo que necesita una instalación de 15 kWp' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: unos ${FACTS.roofAreaM2} m² de tejado y aproximadamente ` +
        `${FACTS.moduleCount} paneles con módulos habituales de ~440 Wp. La producción anual, con ` +
        `orientación sur y sin sombras, ronda los ${FACTS.annualYieldKwh.toLocaleString('es-ES')} kWh. ` +
        'Si la superficie principal del tejado no basta, las empresas instaladoras suelen valorar ' +
        'repartir los paneles entre varias vertientes con distinta orientación - un cálculo exacto ' +
        'para su tejado lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Componentes del coste de una instalación fotovoltaica' },
    {
      type: 'list',
      items: [
        'Paneles: el mayor coste individual, normalmente en torno a un tercio del total',
        'Inversor: con 15 kWp normalmente trifásico, a veces con varias cadenas/inversores',
        'Montaje/mano de obra: fijación en el tejado, cableado',
        'Andamiaje: más extenso cuanto mayor es la superficie de tejado',
        'Instalación eléctrica: adaptación del cuadro eléctrico, conexión a la red de la vivienda',
        'Tramitación: registro ante la distribuidora y organismos correspondientes',
      ],
    },
    {
      type: 'paragraph',
      text:
        'A partir de unos 15 kWp conviene revisar con más detalle la estructura del tejado y la ' +
        'capacidad de la acometida eléctrica - ambos aspectos los valora la empresa instaladora in ' +
        'situ al elaborar el presupuesto.',
    },
    { type: 'heading', text: 'Amortización y autoconsumo' },
    {
      type: 'paragraph',
      text:
        'Con bomba de calor y coche eléctrico como consumidores adicionales, es posible alcanzar un ' +
        'autoconsumo alto en una instalación de 15 kWp, especialmente si ambos equipos funcionan ' +
        'sobre todo durante el día. Un cálculo exacto para su tejado lo ofrece la calculadora de ' +
        'dimensionamiento.',
    },
  ],
  faq: [
    {
      q: '¿Necesito una licencia de obra para una instalación de 15 kWp?',
      a: 'Normalmente no - las instalaciones fotovoltaicas sobre tejados existentes suelen estar exentas de licencia. Hay excepciones, por ejemplo en edificios protegidos; en caso de duda, la empresa instaladora lo comprueba de antemano.',
    },
    {
      q: '¿Para qué edificios es adecuada una instalación de 15 kWp?',
      a: 'Típicamente para viviendas unifamiliares grandes o bifamiliares con un consumo total elevado, por ejemplo por bomba de calor y coche eléctrico juntos.',
    },
    {
      q: '¿Cómo influye el tamaño de la instalación en el coste de montaje?',
      a: 'Las instalaciones más grandes como 15 kWp suelen tener costes fijos por kWp algo más bajos (tramitación, andamiaje básico) que las instalaciones más pequeñas, ya que estos conceptos se reparten entre más potencia instalada.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Instalación fotovoltaica de 12 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-12-kwp-precio' },
    { label: 'Instalación fotovoltaica de 20 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-20-kwp-precio' },
    { label: 'Instalación fotovoltaica de 25 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-25-kwp-precio' },
    { label: 'Batería solar de 15 kWh: precio y valores orientativos', href: '/bateria-solar-15-kwh-precio' },
    { label: 'Planificar la instalación paso a paso', href: '/planificar-instalacion-fotovoltaica' },
  ],
}
